import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { haptic } from "@/lib/telegram";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { categoryLabel } from "@/lib/categories";
import { useImageLook } from "@/lib/useImageFit";
import type { CategoryRef, Product } from "@/types";

export const dsvg = (d: string, size = 18, sw = 1.6, color = "currentColor") => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
);
export const DI = {
  arrow: dsvg('<path d="M5 12h14M13 6l6 6-6 6"/>', 16, 1.8),
  heart: dsvg('<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>', 15, 1.8),
  cart: dsvg('<path d="M3 4h2l2.5 12h11L21 7H6.2"/><circle cx="9.5" cy="19.5" r="1.3"/><circle cx="17.5" cy="19.5" r="1.3"/>', 18, 1.8),
  chevL: dsvg('<path d="M15 6l-6 6 6 6"/>', 16, 1.8),
  chevR: dsvg('<path d="M9 6l6 6-6 6"/>', 16, 1.8),
  check: dsvg('<path d="M5 12.5l4.5 4.5L19 7.5"/>', 18, 2),
};
/**
 * Brand icons: a soft round badge in the warm accent colour with the line icon
 * inside. Colour comes from CSS (--dk-ico), so the evening theme gets gold.
 */
export const WI = (d: string, size = 34) => (
  <svg
    className="dk-ico"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    dangerouslySetInnerHTML={{
      __html: `<circle cx="12" cy="12" r="12" fill="currentColor" opacity=".13"/><circle cx="12" cy="12" r="11.4" fill="none" stroke="currentColor" stroke-opacity=".22" stroke-width=".6"/><g transform="translate(3.6 3.6) scale(.7)" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${d}</g>`,
    }}
  />
);
export const PATH = {
  bean: '<ellipse cx="12" cy="12" rx="6.3" ry="8.8" transform="rotate(35 12 12)"/><path d="M16.3 5.6c-3.4 2.6-1.9 5.6-3.6 7.6-1.4 1.6-3.6 1.9-4.9 4.9"/>',
  leaf: '<path d="M5 19.5c0-8.3 5.2-13.9 14.5-14.5.4 9.3-5.2 14.5-14.5 14.5z"/><path d="M5 19.5c3.4-4.6 6.6-7.7 10.3-10.3"/>',
  truck: '<path d="M2.5 6.5h11v9.5h-11z"/><path d="M13.5 9.5h4.2l3.3 3.4V16h-7.5"/><circle cx="6.5" cy="17.6" r="1.9"/><circle cx="17.2" cy="17.6" r="1.9"/>',
  gear: '<path d="M3 8.5h12v2.2a4.3 4.3 0 0 1-4.3 4.3H7.3A4.3 4.3 0 0 1 3 10.7z"/><path d="M15 9.6h6"/><path d="M7.5 15v2.4M10.5 15v2.4"/><path d="M5.5 20.5h7"/><path d="M7 4.5c-.6.8.6 1.4 0 2.2M10.5 4.5c-.6.8.6 1.4 0 2.2"/>',
  award: '<circle cx="12" cy="9" r="5.6"/><path d="m9.2 13.9-1.5 6.6 4.3-2.3 4.3 2.3-1.5-6.6"/><path d="m9.7 9 1.5 1.5 3-3"/>',
  home: '<path d="M3.5 11 12 4l8.5 7"/><path d="M5.5 9.5V20h13V9.5"/><path d="M10 20v-5.5h4V20"/>',
  check: '<path d="M12 3l7 3v5.5c0 4.4-3 8-7 9.5-4-1.5-7-5.1-7-9.5V6z"/><path d="m8.8 12 2.2 2.2 4.3-4.6"/>',
  people: '<circle cx="9" cy="8.3" r="3.1"/><path d="M3.4 19.5c.6-3.4 2.8-5.4 5.6-5.4s5 2 5.6 5.4"/><circle cx="16.9" cy="9.4" r="2.5"/><path d="M15.7 14.2c2.7-.4 4.7 1.4 5.3 4.7"/>',
};

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/**
 * "Added to cart" feedback on the desktop site: a small dot flies from the
 * clicked button to the cart icon in the header, and the counter bounces.
 */
/** "12 отзывов" / "1 review" with the right plural form. */
export function reviewsText(n: number, locale: string, t: (k: string, v?: Record<string, string | number>) => string) {
  const m10 = n % 10, m100 = n % 100;
  const f = locale === "ru" ? (m10 === 1 && m100 !== 11 ? "one" : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? "few" : "many") : n === 1 ? "one" : "many";
  return t(`desktop.reviews_${f}`, { count: n });
}

export function flyToCart(from: Element | null) {
  const cart = document.querySelector(".dk-cart");
  if (!cart) return;
  const bump = () => {
    cart.classList.remove("dk-cart--bump");
    void (cart as HTMLElement).offsetWidth;
    cart.classList.add("dk-cart--bump");
  };
  if (!from || reducedMotion() || typeof document.body.animate !== "function") return bump();
  const a = from.getBoundingClientRect();
  const b = cart.getBoundingClientRect();
  const dot = document.createElement("span");
  dot.className = "dk-fly";
  dot.style.left = `${a.left + a.width / 2 - 9}px`;
  dot.style.top = `${a.top + a.height / 2 - 9}px`;
  document.body.appendChild(dot);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const anim = dot.animate(
    [
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 90}px) scale(1.15)`, opacity: 1, offset: 0.5 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.4)`, opacity: 0.6 },
    ],
    { duration: 650, easing: "cubic-bezier(.3,.1,.3,1)" },
  );
  anim.onfinish = () => {
    dot.remove();
    bump();
  };
}

/** Grey placeholder in the shape of a product card while the catalogue loads. */
export function DSkeletonCard({ imgHeight = 184 }: { imgHeight?: number }) {
  return (
    <div className="dpc dpc--sk" aria-hidden="true">
      <div className="dk-sk" style={{ height: imgHeight, borderRadius: 0 }} />
      <div className="dpc__body">
        <div className="dk-sk" style={{ height: 14, width: "70%" }} />
        <div className="dk-sk" style={{ height: 11, width: "45%", marginTop: 8 }} />
        <div className="dpc__row" style={{ marginTop: 14 }}>
          <div className="dk-sk" style={{ height: 16, width: "40%" }} />
          <div className="dk-sk" style={{ height: 40, width: 40, borderRadius: "50%" }} />
        </div>
      </div>
    </div>
  );
}

/** Desktop product card (reference style): photo, heart, name, subtitle, price, cart or request button. */
export function DProductCard({ product, imgHeight = 184 }: { product: Product; imgHeight?: number }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { addItem, isFavorite, toggleFavorite } = useCart();
  const [added, setAdded] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const v = product.variants[0];
  const underOrder = product.orderMode === "under_order";
  const inStock = (v?.stock ?? 1) > 0;
  const img = mediaUrl(product.images[0], 400);
  const look = useImageLook(img);
  const sub = product.category === "coffee" ? `${categoryLabel(product, t)} · ${v?.label ?? ""}` : product.shortDescription;
  return (
    <div className="dpc" onClick={() => navigate({ name: "product", slug: product.slug })}>
      <div className="dpc__img" data-fit={look.fit} style={{ height: imgHeight, ...(look.bg ? { background: look.bg } : {}) }}>
        {img && <img src={img} alt={product.name} loading="lazy" />}
        <button
          className="dpc__qv"
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(new CustomEvent("kms:quick-view", { detail: product.slug }));
          }}
        >
          {t("desktop.quickView")}
        </button>
        <button
          className="dpc__heart"
          data-on={isFavorite(product.id)}
          aria-label={t("profile.favorites")}
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(product.id);
          }}
        >
          {DI.heart}
        </button>
      </div>
      <div className="dpc__body">
        <div className="dpc__name">{product.name}</div>
        <div className="dpc__sub">{sub}</div>
        {underOrder && v && v.priceUzs > 0 && <div className="dpc__ask">{formatUzs(v.priceUzs, locale)}</div>}
        <div className="dpc__row">
          {underOrder ? (
            <span className="dpc__muted">{v && v.priceUzs > 0 ? t("catalog.underOrder") : t("catalog.onRequest")}</span>
          ) : (
            <span className="dpc__price">{v ? formatUzs(v.priceUzs, locale) : ""}</span>
          )}
          {underOrder ? (
            <span className="dpc__lead">{t("desktop.request")}</span>
          ) : (
            <button
              className="dpc__cart"
              disabled={!inStock}
              aria-label={t("product.addToCart")}
              onClick={(e) => {
                e.stopPropagation();
                if (!v) return;
                haptic("light");
                addItem(product.id, v.id, 1);
                flyToCart(e.currentTarget);
                setAdded(true);
                window.clearTimeout(timer.current);
                timer.current = window.setTimeout(() => setAdded(false), 1400);
              }}
              data-added={added}
            >
              {added ? DI.check : DI.cart}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Top navigation as on the reference: coffee categories, "Equipment", "Accessories", blog, about, B2B. */
/** Does a product belong to a catalogue "category" value (a real category slug or a group pseudo-slug)? */
export function inSlug(p: Product, slug: string, matchCategory: (p: Product, slug: string) => boolean): boolean {
  const kinds = GROUP_KINDS[slug];
  return kinds ? kinds.includes(p.category) : matchCategory(p, slug);
}

export const GROUP_EQUIPMENT = "group-equipment";
export const GROUP_ACCESSORIES = "group-accessories";
/** Product kinds that make up a navigation group. */
export const GROUP_KINDS: Record<string, string[]> = {
  [GROUP_EQUIPMENT]: ["machines", "grinders"],
  [GROUP_ACCESSORIES]: ["accessories", "parts"],
};
export interface NavGroup {
  /** catalogue "category" value: a group pseudo-slug */
  slug: string;
  /** categories inside the group (for photos and counts) */
  categories: CategoryRef[];
}

/**
 * Top navigation as on the reference: each coffee category, then "Equipment"
 * (machines + grinders) and "Accessories" (e.g. Scales) as whole groups.
 */
export function useDesktopNav(categories: CategoryRef[] | null) {
  const list = categories ?? [];
  const coffee = list.filter((c) => c.kind === "coffee");
  const group = (slug: string): NavGroup | null => {
    const cats = list.filter((c) => GROUP_KINDS[slug].includes(c.kind));
    return cats.length ? { slug, categories: cats } : null;
  };
  return { coffee, equipment: group(GROUP_EQUIPMENT), accessories: group(GROUP_ACCESSORIES) };
}
